// purpose: R2の134 BPM音楽クロック・ステージ進行・戦闘状態を読み、拍頭へ収束するリング／レール／フレーム演出を描く。

import { Graphics, GraphicsContext } from 'pixi.js';
import { EnemyState, EnemyType, GameMode, POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { RendererSettings } from '../../port';
import { R2_HEIGHT, R2_WIDTH } from '../R2Renderer';
import { WORLD_TO_R2 } from '../scale';

/** AudioEngine が構造的に満たす読み取り専用境界。描画側から音声ノードへは触れない。 */
export interface R2MusicVisualClock {
  readonly usesHTMLMediaFallback?: boolean;
  r2MusicStepPosition(): number;
}

export interface R2MusicVisualHost {
  setMusicPulseZoom(scale: number): void;
  setMusicPulse(level: number): void;
}

const STEP_LEAD = .11;
const COMBO_ANTICIPATION_STEPS = .55;
const RING_SLOTS = 6;
const NO_STEP = -1;
const NO_EVENT = 0;
const EVENT_SHOT = 1;
const EVENT_SMALL_KILL = 2;
const EVENT_LARGE_KILL = 3;
const EVENT_COMBO = 4;
const BIG_ENEMY_MIN_TYPE = EnemyType.ChoirEngine;

const COMBO_TIER_CHAINS: readonly number[] = Object.freeze([0, 5, 10, 20]);
const CYAN = 0x83f5ff;
const ICE = 0xd9fbff;
const AMBER = 0xffd27a;
const MAGENTA = 0xff65d8;
const SHADE = 0x03101a;
const STAGE_CLIMAX = 2;

const DURATION_MS = new Float32Array([0, 120, 175, 245, 300]);
const START_RADIUS = new Float32Array([0, 4, 6, 10, 16]);
const END_RADIUS = new Float32Array([0, 34, 58, 138, 270]);
const BASE_ALPHA = new Float32Array([0, .56, .76, .96, 1]);
const COLOR = new Uint32Array([0, CYAN, ICE, AMBER, ICE]);
const ZOOM_BY_EVENT = new Float32Array([0, .008, .016, .045, .068]);
const MUSIC_PULSE_MAX = .068;
const MUSIC_PULSE_TIME_CONSTANT_MS = 88;
const MUSIC_PULSE_EPSILON = .0002;
const WASH_DURATION_MS = 72;

export function createMobileMusicRailContexts(): readonly [GraphicsContext, GraphicsContext] {
  const normal = new GraphicsContext()
    .rect(0, R2_HEIGHT / 2 - 1, R2_WIDTH, 2)
    .rect(8, 7, R2_WIDTH - 16, 1)
    .rect(8, R2_HEIGHT - 9, R2_WIDTH - 16, 1)
    .fill(0xffffff);
  const climax = new GraphicsContext()
    .rect(0, R2_HEIGHT / 2 - 1, R2_WIDTH, 2)
    .rect(8, 7, R2_WIDTH - 16, 2)
    .rect(8, R2_HEIGHT - 9, R2_WIDTH - 16, 2)
    .fill(0xffffff);
  normal.autoGarbageCollect = false;
  climax.autoGarbageCollect = false;
  return [normal, climax];
}

export class MusicVisualDirector {
  private field: Graphics | null = null;
  private shade: Graphics | null = null;
  private lightweightRailContexts: readonly [GraphicsContext, GraphicsContext] | null = null;
  private lastGridStep = NO_STEP;
  private beatAgeMs = Number.POSITIVE_INFINITY;
  private stepAgeMs = Number.POSITIVE_INFINITY;
  private entranceAgeMs = Number.POSITIVE_INFINITY;
  private lastStage = NO_STEP;
  private prevChainTier = 0;
  private prevBossShake = 0;
  private pendingKind = NO_EVENT;
  private pendingStep = NO_STEP;
  private pendingX = R2_WIDTH / 2;
  private pendingY = R2_HEIGHT / 2;
  private ringCursor = 0;
  private pulseExcess = 0;
  private washKind = NO_EVENT;
  private washAgeMs = Number.POSITIVE_INFINITY;

  private readonly prevEnemyState = new Uint8Array(POOL_CAPACITY.enemies);
  private readonly ringKind = new Uint8Array(RING_SLOTS);
  private readonly ringX = new Float32Array(RING_SLOTS);
  private readonly ringY = new Float32Array(RING_SLOTS);
  private readonly ringAgeMs = new Float32Array(RING_SLOTS);

  constructor(
    private readonly clock: R2MusicVisualClock,
    private readonly host: R2MusicVisualHost,
    private readonly lightweight = false,
  ) {}

  init(
    field: Graphics,
    shade: Graphics,
    lightweightRailContexts: readonly [GraphicsContext, GraphicsContext] | null = null,
  ): void {
    this.field = field;
    this.shade = shade;
    this.lightweightRailContexts = lightweightRailContexts;
    if (this.lightweight) {
      field.visible = false;
      shade.visible = false;
    }
  }

  sync(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    const field = this.field;
    const shade = this.shade;
    if (!field || !shade) return;
    if (this.lightweight) {
      this.syncLightweight(world, deltaMs, settings, field, shade);
      return;
    }
    field.clear();
    shade.clear();
    if (world.mode !== GameMode.Playing) {
      this.lastGridStep = NO_STEP;
      this.host.setMusicPulseZoom(1);
      this.host.setMusicPulse(0);
      return;
    }

    const stepPosition = this.clock.r2MusicStepPosition();
    if (stepPosition < 0) return;
    if (world.currentStage !== this.lastStage) {
      this.lastStage = world.currentStage;
      this.entranceAgeMs = 0;
    }
    this.captureKills(world, stepPosition);
    this.captureCombo(world, stepPosition);
    this.captureBossImpact(world, stepPosition);

    const gridStep = Math.floor(stepPosition);
    if (gridStep !== this.lastGridStep) {
      this.lastGridStep = gridStep;
      this.stepAgeMs = 0;
      if ((gridStep & 3) === 0) this.beatAgeMs = 0;
      const released = this.releasePending(gridStep);
      if (!released && world.player.fireCooldown > 0) {
        this.spawn(EVENT_SHOT, world.player.x * WORLD_TO_R2 + 20, world.player.y * WORLD_TO_R2);
      }
    }

    this.drawAnticipation(stepPosition, settings);
    this.drawStageEntrance(world, deltaMs, settings);
    this.drawReleaseWash(world, deltaMs, settings);
    this.drawStepDrive(world, deltaMs, settings);
    this.drawBeatField(world, deltaMs, settings);
    this.drawRings(world, deltaMs, settings);
    this.applyMusicPulse(world, deltaMs, settings);
  }

  onSceneReset(): void {
    this.lastGridStep = NO_STEP;
    this.beatAgeMs = Number.POSITIVE_INFINITY;
    this.stepAgeMs = Number.POSITIVE_INFINITY;
    this.entranceAgeMs = Number.POSITIVE_INFINITY;
    this.lastStage = NO_STEP;
    this.prevChainTier = 0;
    this.prevBossShake = 0;
    this.pendingKind = NO_EVENT;
    this.pendingStep = NO_STEP;
    this.pulseExcess = 0;
    this.washKind = NO_EVENT;
    this.washAgeMs = Number.POSITIVE_INFINITY;
    this.prevEnemyState.fill(EnemyState.Free);
    this.ringKind.fill(NO_EVENT);
    this.host.setMusicPulseZoom(1);
    this.host.setMusicPulse(0);
  }

  dispose(): void {
    this.field = null;
    this.shade = null;
    this.lightweightRailContexts = null;
    this.ringKind.fill(NO_EVENT);
    this.host.setMusicPulseZoom(1);
    this.host.setMusicPulse(0);
  }

  /**
   * HTMLAudio を使うiPhone向け。全敵走査、毎フレームのGraphics再構築、
   * 全画面色収差を止め、四分音符ごとの固定レールをalphaだけで減衰させる。
   */
  private syncLightweight(
    world: GameWorld,
    deltaMs: number,
    settings: RendererSettings,
    field: Graphics,
    shade: Graphics,
  ): void {
    shade.visible = false;
    this.host.setMusicPulseZoom(1);
    this.host.setMusicPulse(0);
    if (world.mode !== GameMode.Playing) {
      if (field.visible) {
        field.visible = false;
      }
      this.lastGridStep = NO_STEP;
      this.beatAgeMs = Number.POSITIVE_INFINITY;
      return;
    }

    const stepPosition = this.clock.r2MusicStepPosition();
    if (stepPosition < 0) {
      field.visible = false;
      return;
    }
    const gridStep = Math.floor(stepPosition);
    if (gridStep !== this.lastGridStep) {
      this.lastGridStep = gridStep;
      if ((gridStep & 3) === 0) {
        this.beatAgeMs = 0;
        const stageEnergy = visualStage(world);
        const color = stageColor(stageEnergy, gridStep >> 2);
        const contexts = this.lightweightRailContexts;
        if (contexts) {
          field.context = contexts[stageEnergy >= STAGE_CLIMAX ? 1 : 0];
          field.tint = color;
        } else {
          field.clear();
          field.rect(0, R2_HEIGHT / 2 - 1, R2_WIDTH, 2);
          field.rect(8, 7, R2_WIDTH - 16, stageEnergy >= STAGE_CLIMAX ? 2 : 1);
          field.rect(8, R2_HEIGHT - 9, R2_WIDTH - 16, stageEnergy >= STAGE_CLIMAX ? 2 : 1);
          field.fill({ color, alpha: 1 });
        }
        field.visible = true;
      }
    }

    this.beatAgeMs += deltaMs;
    const duration = 118;
    const reduction = settings.flashReduction ? .42 : 1;
    field.alpha = Math.max(0, 1 - this.beatAgeMs / duration) * .24 * reduction;
    if (field.alpha <= 0) field.visible = false;
  }

  private captureKills(world: GameWorld, stepPosition: number): void {
    const pool = world.enemies;
    const active = pool.indices.active;
    for (let i = 0; i < this.prevEnemyState.length; i += 1) {
      if (active[i] === 0) {
        this.prevEnemyState[i] = EnemyState.Free;
        continue;
      }
      const state = pool.state[i]!;
      const wasDying = this.prevEnemyState[i] === EnemyState.Dying;
      this.prevEnemyState[i] = state;
      if (state !== EnemyState.Dying || wasDying) continue;
      const kind = pool.type[i]! >= BIG_ENEMY_MIN_TYPE ? EVENT_LARGE_KILL : EVENT_SMALL_KILL;
      const multiple = kind === EVENT_LARGE_KILL ? 2 : 1;
      const target = nextGridStep(stepPosition, multiple);
      this.queue(kind, target, pool.x[i]! * WORLD_TO_R2, pool.y[i]! * WORLD_TO_R2);
    }
  }

  private captureCombo(world: GameWorld, stepPosition: number): void {
    const tier = comboTier(world.player.chain);
    if (tier > this.prevChainTier) {
      this.queue(
        EVENT_COMBO,
        nextGridStep(stepPosition, 4),
        world.player.x * WORLD_TO_R2,
        world.player.y * WORLD_TO_R2,
      );
    }
    this.prevChainTier = tier;
  }

  private captureBossImpact(world: GameWorld, stepPosition: number): void {
    const ticks = world.screenShakeTicks;
    if (ticks >= 24 && ticks > this.prevBossShake) {
      this.queue(EVENT_COMBO, nextGridStep(stepPosition, 4), R2_WIDTH / 2, R2_HEIGHT / 2);
    }
    this.prevBossShake = ticks;
  }

  private queue(kind: number, targetStep: number, x: number, y: number): void {
    if (this.pendingStep === targetStep && this.pendingKind > kind) return;
    if (this.pendingStep !== NO_STEP && this.pendingStep < targetStep && this.pendingKind >= kind) return;
    this.pendingKind = kind;
    this.pendingStep = targetStep;
    this.pendingX = x;
    this.pendingY = y;
  }

  private releasePending(gridStep: number): boolean {
    if (this.pendingKind === NO_EVENT || gridStep < this.pendingStep) return false;
    this.spawn(this.pendingKind, this.pendingX, this.pendingY);
    this.pendingKind = NO_EVENT;
    this.pendingStep = NO_STEP;
    return true;
  }

  private spawn(kind: number, x: number, y: number): void {
    const slot = this.ringCursor;
    this.ringCursor = (slot + 1) % RING_SLOTS;
    this.ringKind[slot] = kind;
    this.ringX[slot] = x;
    this.ringY[slot] = y;
    this.ringAgeMs[slot] = 0;
    this.pulseExcess = Math.max(this.pulseExcess, ZOOM_BY_EVENT[kind] ?? 0);
    if (kind >= EVENT_LARGE_KILL) {
      this.washKind = kind;
      this.washAgeMs = 0;
    }
  }

  private drawAnticipation(stepPosition: number, settings: RendererSettings): void {
    if (this.pendingKind !== EVENT_COMBO || this.pendingStep < 0) return;
    const until = this.pendingStep - stepPosition;
    if (until < 0 || until > COMBO_ANTICIPATION_STEPS) return;
    const strength = settings.flashReduction ? .09 : .24;
    const alpha = strength * (1 - until / COMBO_ANTICIPATION_STEPS);
    const shade = this.shade;
    if (!shade || alpha <= 0) return;
    shade.rect(0, 0, R2_WIDTH, R2_HEIGHT);
    shade.fill({ color: SHADE, alpha });
  }

  private drawStageEntrance(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    this.entranceAgeMs += deltaMs;
    const duration = 1050;
    if (this.entranceAgeMs >= duration) return;
    const field = this.field;
    const shade = this.shade;
    if (!field || !shade) return;
    const stageEnergy = visualStage(world);
    const phase = this.entranceAgeMs / duration;
    const eased = 1 - (1 - phase) * (1 - phase);
    const reduction = settings.flashReduction ? .4 : 1;
    const color = stageColor(stageEnergy, world.currentStage);
    const alpha = (1 - phase) * (.12 + stageEnergy * .1) * reduction;
    const halfWidth = Math.round((R2_WIDTH / 2 - 10) * eased);
    const halfHeight = Math.round((R2_HEIGHT / 2 - 8) * eased);
    field.rect(
      R2_WIDTH / 2 - halfWidth,
      R2_HEIGHT / 2 - halfHeight,
      halfWidth * 2,
      halfHeight * 2,
    );
    field.stroke({ color, alpha, width: stageEnergy >= STAGE_CLIMAX ? 3 : 2 });
    field.rect(0, R2_HEIGHT / 2 - 2, R2_WIDTH, 4);
    field.fill({ color, alpha: alpha * .46 });
    if (stageEnergy >= 1 && phase < .28) {
      shade.rect(0, 0, R2_WIDTH, R2_HEIGHT);
      shade.fill({ color, alpha: (1 - phase / .28) * .13 * stageEnergy * reduction });
    }
  }

  private drawReleaseWash(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    this.washAgeMs += deltaMs;
    if (this.washKind < EVENT_LARGE_KILL || this.washAgeMs >= WASH_DURATION_MS) return;
    const shade = this.shade;
    if (!shade) return;
    const phase = this.washAgeMs / WASH_DURATION_MS;
    const base = this.washKind === EVENT_COMBO ? .24 : .17;
    const reduction = settings.flashReduction ? .35 : 1;
    const alpha = (1 - phase) * (base + visualStage(world) * .035) * reduction;
    shade.rect(0, 0, R2_WIDTH, R2_HEIGHT);
    shade.fill({ color: COLOR[this.washKind]!, alpha });
  }

  private drawStepDrive(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    this.stepAgeMs += deltaMs;
    const stageEnergy = visualStage(world);
    if (stageEnergy < 1 || this.stepAgeMs >= 72) return;
    const field = this.field;
    if (!field) return;
    const phase = this.stepAgeMs / 72;
    const reduction = settings.flashReduction ? .42 : 1;
    const alpha = (1 - phase) * (.12 + stageEnergy * .075) * reduction;
    const color = stageColor(stageEnergy, this.lastGridStep);
    const y = 18 + ((this.lastGridStep * 23) % (R2_HEIGHT - 36));
    const length = Math.round((30 + stageEnergy * 34) * (1 - phase * .35));
    field.rect(0, y, length, stageEnergy >= STAGE_CLIMAX ? 3 : 2);
    field.rect(R2_WIDTH - length, R2_HEIGHT - y, length, stageEnergy >= STAGE_CLIMAX ? 3 : 2);
    field.fill({ color, alpha });
    if (stageEnergy >= STAGE_CLIMAX) {
      const horizonY = Math.round(R2_HEIGHT / 2 + Math.sin(this.lastGridStep * 1.57) * 36);
      const inset = Math.round(phase * R2_WIDTH * .28);
      field.rect(inset, horizonY, R2_WIDTH - inset * 2, 1);
      field.fill({ color, alpha: alpha * .72 });
      field.rect(R2_WIDTH / 2 - 1, 0, 2, Math.max(1, Math.round(28 * (1 - phase))));
      field.rect(R2_WIDTH / 2 - 1, R2_HEIGHT - Math.max(1, Math.round(28 * (1 - phase))), 2, Math.max(1, Math.round(28 * (1 - phase))));
      field.fill({ color: ICE, alpha: alpha * .58 });
    }
  }

  private drawBeatField(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    this.beatAgeMs += deltaMs;
    const stageEnergy = visualStage(world);
    const duration = 115 + stageEnergy * 34;
    if (this.beatAgeMs >= duration) return;
    const field = this.field;
    if (!field) return;
    const phase = this.beatAgeMs / duration;
    const reduction = settings.flashReduction ? .45 : 1;
    const alpha = (1 - phase) * (.17 + stageEnergy * .075) * reduction;
    const inset = Math.round(4 + phase * 24);
    const color = stageColor(stageEnergy, this.lastGridStep >> 2);
    field.rect(inset, R2_HEIGHT / 2 - 1, R2_WIDTH - inset * 2, 2);
    field.fill({ color, alpha: alpha * .72 });
    field.rect(inset, 10, 2, R2_HEIGHT - 20);
    field.rect(R2_WIDTH - inset - 2, 10, 2, R2_HEIGHT - 20);
    field.fill({ color, alpha });
    if (stageEnergy >= 1) {
      const railInset = Math.round(12 + phase * 52);
      field.rect(railInset, 8, R2_WIDTH - railInset * 2, 2);
      field.rect(railInset, R2_HEIGHT - 10, R2_WIDTH - railInset * 2, 2);
      field.fill({ color: stageEnergy >= STAGE_CLIMAX ? MAGENTA : AMBER, alpha: alpha * .72 });
    }
    if (stageEnergy >= STAGE_CLIMAX) {
      for (let i = 0; i < 3; i += 1) {
        const frameInset = Math.round(16 + i * 22 + phase * (34 + i * 9));
        const frameTop = Math.round(10 + i * 13 + phase * 12);
        const frameWidth = R2_WIDTH - frameInset * 2;
        const frameHeight = R2_HEIGHT - frameTop * 2;
        if (frameWidth <= 0 || frameHeight <= 0) continue;
        field.rect(frameInset, frameTop, frameWidth, frameHeight);
        field.stroke({ color: i === 1 ? MAGENTA : CYAN, alpha: alpha * (.62 - i * .12), width: i === 0 ? 2 : 1 });
      }
    }
  }

  private drawRings(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    const field = this.field;
    if (!field) return;
    const reduction = settings.flashReduction ? .48 : 1;
    const stageEnergy = visualStage(world);
    for (let i = 0; i < RING_SLOTS; i += 1) {
      const kind = this.ringKind[i]!;
      if (kind === NO_EVENT) continue;
      const age = this.ringAgeMs[i]! + deltaMs;
      this.ringAgeMs[i] = age;
      const duration = DURATION_MS[kind]!;
      if (age >= duration) {
        this.ringKind[i] = NO_EVENT;
        continue;
      }
      const phase = age / duration;
      const eased = 1 - (1 - phase) * (1 - phase);
      const radius = (START_RADIUS[kind]! + (END_RADIUS[kind]! - START_RADIUS[kind]!) * eased) * (1 + stageEnergy * .08);
      const alpha = Math.min(1, BASE_ALPHA[kind]! * (1 + stageEnergy * .09)) * (1 - phase) * reduction;
      const width = kind >= EVENT_LARGE_KILL && phase < .32 ? 2 : 1;
      field.circle(Math.round(this.ringX[i]!), Math.round(this.ringY[i]!), Math.max(1, Math.round(radius)));
      field.stroke({ color: COLOR[kind]!, alpha, width });
      if (kind === EVENT_SHOT) {
        const x = Math.round(this.ringX[i]!);
        const y = Math.round(this.ringY[i]!);
        field.rect(x, y - 1, Math.max(2, R2_WIDTH - x), phase < .35 ? 2 : 1);
        field.fill({ color: COLOR[kind]!, alpha: alpha * .78 });
      }
      if (kind >= EVENT_LARGE_KILL && phase > .16) {
        field.circle(
          Math.round(this.ringX[i]!),
          Math.round(this.ringY[i]!),
          Math.max(1, Math.round(radius * .58)),
        );
        field.stroke({ color: COLOR[kind]!, alpha: alpha * .42, width: 1 });
      }
    }
  }

  private applyMusicPulse(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    const reduction = settings.flashReduction ? .55 : 1;
    const stageEnergy = visualStage(world);
    const beatDuration = 115 + stageEnergy * 34;
    const beatPhase = Math.min(1, this.beatAgeMs / beatDuration);
    const beatPulse = (1 - beatPhase) * (.08 + stageEnergy * .13);
    // 音楽同期も全景スケールは固定し、発光／色収差だけで拍を見せる。
    this.host.setMusicPulseZoom(1);
    this.host.setMusicPulse(Math.min(1, Math.max(this.pulseExcess / MUSIC_PULSE_MAX, beatPulse)) * reduction);
    this.pulseExcess *= Math.exp(-deltaMs / MUSIC_PULSE_TIME_CONSTANT_MS);
    if (this.pulseExcess < MUSIC_PULSE_EPSILON) this.pulseExcess = 0;
  }
}

function nextGridStep(stepPosition: number, multiple: number): number {
  return Math.ceil((stepPosition + STEP_LEAD) / multiple) * multiple;
}

function comboTier(chain: number): number {
  if (chain >= COMBO_TIER_CHAINS[3]!) return 3;
  if (chain >= COMBO_TIER_CHAINS[2]!) return 2;
  if (chain >= COMBO_TIER_CHAINS[1]!) return 1;
  return 0;
}

function visualStage(world: GameWorld): number {
  return Math.max(0, Math.min(STAGE_CLIMAX, world.currentStage | 0));
}

function stageColor(stageEnergy: number, phase: number): number {
  if (stageEnergy >= STAGE_CLIMAX) return (phase & 1) === 0 ? CYAN : MAGENTA;
  if (stageEnergy >= 1) return (phase & 1) === 0 ? CYAN : AMBER;
  return CYAN;
}
